import { useState } from "react";
import HeightStuffingImage from "assets/images/package-settings/sacks/height-stuffing.svg?react";
import LayersCountImage from "assets/images/package-settings/sacks/layers-count.svg?react";
import MassImage from "assets/images/package-settings/sacks/mass.svg?react";
import TillToLengthImage from "assets/images/package-settings/sacks/till-to-length.svg?react";
import TillToWidthImage from "assets/images/package-settings/sacks/till-to-width.svg?react";
import { CustomCheckbox } from "components/UI/CustomCheckbox";
import { Input } from "components/UI/Input";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useTranslation } from "localization/localization";
import { SacksSettings } from "models/packageSettings";
import { selectOptions, updateCargoRow } from "store";
import { parseInputNumber } from "utils/parseInputNumber.utils";
import { fromDisplayLength, fromDisplayWeight, toDisplayLength, toDisplayWeight } from "utils/units.utils";
import { SacksContentProps } from "../PackageSettings.types";
import { PackageSettingsLayout } from "../PackageSettingLayout";
import {
  applyDisableStacking,
  applyStuffingSelect,
  SETTINGS_NUMBER_PLACEHOLDER,
  toSettingsInputValue,
} from "../PackageSettings.helpers";
import s from "../PackageSettings.module.scss";

type Fields = Partial<Omit<SacksSettings, "type">>;
type StuffingField = keyof Pick<SacksSettings, "layersCount" | "mass" | "heightStuffing">;

const STUFFING_FIELDS: StuffingField[] = ["layersCount", "mass", "heightStuffing"];

export const SacksContent = ({ settings: initialSettings, groupId, rowId, onClose }: SacksContentProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { lengthUnits, weightUnits } = useAppSelector(selectOptions);
  const [settings, setSettings] = useState<SacksSettings>(initialSettings);

  const update = (changes: Fields) => setSettings((prev) => ({ ...prev, ...changes }));

  const handleStuffingSelect = (entryField: StuffingField) => {
    setSettings((prev) => applyStuffingSelect(prev, STUFFING_FIELDS, entryField));
  };

  const handleDisableStacking = (checked: boolean) => {
    setSettings((prev) => applyDisableStacking(prev, STUFFING_FIELDS, checked));
  };

  const handleSave = () => {
    dispatch(updateCargoRow({ groupId, rowId, packageSettings: settings }));
    onClose();
  };

  return (
    <PackageSettingsLayout
      onClose={onClose}
      onSave={handleSave}
      spacingContent={
        <>
          <div className={s.item}>
            <TillToLengthImage />
            <CustomCheckbox
              checked={settings.tiltToLength}
              label={t("Tilt to length")}
              onChange={(checked) => update({ tiltToLength: checked })}
            />
          </div>

          <div className={s.item}>
            <TillToWidthImage />
            <CustomCheckbox
              checked={settings.tiltToWidth}
              label={t("Tilt to width")}
              onChange={(checked) => update({ tiltToWidth: checked })}
            />
          </div>
        </>
      }
      stuffingContent={
        <>
          <div className={s.item}>
            <LayersCountImage />
            <div className={s.fieldset}>
              <CustomCheckbox
                checked={settings.layersCount.enabled}
                label={t("Layers count")}
                onChange={() => handleStuffingSelect("layersCount")}
              />
              <Input
                type="number"
                placeholder={SETTINGS_NUMBER_PLACEHOLDER}
                value={toSettingsInputValue(settings.layersCount.value)}
                disabled={!settings.layersCount.enabled}
                onChange={(e) =>
                  update({ layersCount: { ...settings.layersCount, value: parseInputNumber(e.target.value) } })
                }
              />
            </div>
          </div>

          <div className={s.item}>
            <MassImage />
            <div className={s.fieldset}>
              <CustomCheckbox
                checked={settings.mass.enabled}
                label={t(`Mass, ${weightUnits}`)}
                onChange={() => handleStuffingSelect("mass")}
              />
              <Input
                type="number"
                placeholder={SETTINGS_NUMBER_PLACEHOLDER}
                value={toSettingsInputValue(toDisplayWeight(settings.mass.value, weightUnits))}
                disabled={!settings.mass.enabled}
                onChange={(e) =>
                  update({
                    mass: { ...settings.mass, value: fromDisplayWeight(parseInputNumber(e.target.value), weightUnits) },
                  })
                }
              />
            </div>
          </div>

          <div className={s.item}>
            <HeightStuffingImage />
            <div className={s.fieldset}>
              <CustomCheckbox
                checked={settings.heightStuffing.enabled}
                label={t(`Height, ${lengthUnits}`)}
                onChange={() => handleStuffingSelect("heightStuffing")}
              />
              <Input
                type="number"
                placeholder={SETTINGS_NUMBER_PLACEHOLDER}
                value={toSettingsInputValue(toDisplayLength(settings.heightStuffing.value, lengthUnits))}
                disabled={!settings.heightStuffing.enabled}
                onChange={(e) =>
                  update({
                    heightStuffing: {
                      ...settings.heightStuffing,
                      value: fromDisplayLength(parseInputNumber(e.target.value), lengthUnits),
                    },
                  })
                }
              />
            </div>
          </div>

          <div className={s.item}>
            <div className={s.fieldset}>
              <CustomCheckbox
                checked={settings.disableStacking}
                label={t("Disable stacking")}
                onChange={handleDisableStacking}
              />
            </div>
          </div>
        </>
      }
    />
  );
};
